import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReadAiChatFigureRowsQuery } from 'store/api/aiChatFigureApi';
import type { FigureDefinition, FigureRows } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { dateStringToDateObject } from 'utils/parseDate';
import { type TableFileFormat } from '../../../utils/tableExport';
import TableFileCard from '../TableFileCard';
import { FigureDialog } from './FigureDialog';
import { FigureDownload, useFigureExport } from './FigureDownload';
import { FigureFrame } from './FigureFrame';
import { FigureList } from './FigureList';
import { columnLabel, figureCount, figureNoun, figureSort, figureTile } from './figureMeta';
import { inlineFigureRows } from './figureRows';
import { FigureTable } from './FigureTable';

/** Where the message keeps its file cards: above the answer's opening text, which they lead. */
export const FileCardSlot = createContext<HTMLElement | null>(null);

// A table shows its first ten rows; "Show more" and the full-size dialog show the rest.
const TABLE_ROWS = 10;

const STATE_CLASS_NAME = 'text-meta text-foreground-secondary flex flex-wrap items-center gap-2 px-4 pb-4';

const SKELETON_BARS = (
    <>
        <div className="bg-muted h-4 w-3/4 rounded-sm" />
        <div className="bg-muted h-4 w-1/2 rounded-sm" />
        <div className="bg-muted h-4 w-2/3 rounded-sm" />
    </>
);

interface RevealProps {
    loading: boolean;
    children: ReactNode;
    /** The bars, in the shape of what comes; three lines of text by default. */
    skeleton?: ReactNode;
    /** How the bars lay out in the slot. */
    skeletonClassName?: string;
}

/**
 * Skeleton reveal: bars pulse while the rows load, then the bars and the rows cross-blur in the same slot. A Figure
 * whose rows are there when it mounts shows them at once.
 */
export function FigureReveal({
    loading,
    children,
    skeleton = SKELETON_BARS,
    skeletonClassName = 'flex flex-col gap-2 px-4 pt-1 pb-4',
}: RevealProps) {
    const [waited] = useState(loading);
    const [revealed, setRevealed] = useState(!loading);

    useEffect(() => {
        if (loading || revealed) {
            return undefined;
        }
        // A frame after the rows land, so they mount hidden and the recipe has a state to move from.
        const frame = requestAnimationFrame(() => setRevealed(true));
        return () => cancelAnimationFrame(frame);
    }, [loading, revealed]);

    if (!waited) {
        return <>{children}</>;
    }
    // The recipe stacks both layers in one slot; the one in flow sizes it: the bars until the reveal, then the rows.
    return (
        <div className={cn('t-skel', revealed && 'is-revealed')}>
            <div
                aria-hidden="true"
                className={cn('t-skel-skeleton', skeletonClassName, !revealed && 'is-pulsing relative!')}
            >
                {skeleton}
            </div>
            {loading ? null : <div className={cn('t-skel-content', revealed && 'relative!')}>{children}</div>}
        </div>
    );
}

interface StatusProps {
    definition: FigureDefinition;
    /** The source answered with no rows. */
    empty: boolean;
    query: { error?: unknown; isError: boolean; refetch: () => unknown };
    /** The failure's words, where the kind's would name the wrong thing: a part of a Figure that did load. */
    failed?: string;
}

/** What a Figure says where its rows would be: none match, the viewer may open none, or the read failed, with Retry. */
export function FigureStatus({ definition, empty, query, failed }: StatusProps) {
    const intl = useIntl();
    const { error } = query;
    if (empty) {
        return (
            <p className={STATE_CLASS_NAME}>
                {intl.formatMessage({ id: 'ai-chat.figure.none' }, { noun: figureNoun(intl, definition) })}
            </p>
        );
    }
    if (typeof error === 'object' && error !== null && 'status' in error && error.status === 403) {
        return <p className={STATE_CLASS_NAME}>{intl.formatMessage({ id: 'ai-chat.figure.no-access' })}</p>;
    }
    if (!query.isError) {
        return null;
    }
    return (
        <div className={STATE_CLASS_NAME}>
            {failed ?? intl.formatMessage({ id: 'ai-chat.figure.failed' }, { kind: definition.kind })}
            <Button
                variant="outline"
                onClick={() => void query.refetch()}
                className="text-meta border-border-control bg-card text-foreground hover:bg-muted h-8 rounded-lg px-3 shadow-none"
            >
                {intl.formatMessage({ id: 'ai-chat.retry' })}
            </Button>
        </div>
    );
}

interface Props {
    definition: FigureDefinition;
    /** The file the user asked for, from the file marker after this Figure's: its card waits for the rows. */
    fileFormat?: TableFileFormat;
}

/**
 * A table or a list Figure: its rows read again for the viewer from its source, or an inline table's own, in the Figure
 * frame with its states. A table offers Download and Open full size; a list neither.
 */
export function RowsFigure({ definition, fileFormat }: Props) {
    const intl = useIntl();
    const fileCardSlot = useContext(FileCardSlot);
    const inline = useMemo(() => inlineFigureRows(definition), [definition]);
    const query = useReadAiChatFigureRowsQuery(definition, { skip: !definition.source });
    const rows: FigureRows | undefined = inline ?? query.data;
    const tableTitle = definition.title ?? '';
    const isTable = definition.kind === 'table';

    const count = rows ? figureCount(intl, definition, rows) : '';
    // The file and the copied CSV say the rows were cut, as the meta line does.
    const { fileName, copyCsv, generating, download } = useFigureExport(tableTitle, rows, rows?.partial ? count : null);

    const tile = figureTile(definition);
    const sort = rows ? figureSort(intl, definition, rows) : null;
    const asOf = !definition.source && definition.asOf ? definition.asOf : null;
    const meta = (
        <>
            {count}
            {sort ? ` · ${sort}` : null}
            {asOf ? (
                <>
                    {' · '}
                    <FormattedMessage
                        id="ai-chat.figure.as-of"
                        values={{ date: <FormattedDate value={dateStringToDateObject(asOf)} /> }}
                    />
                </>
            ) : null}
        </>
    );
    const header = { icon: tile.icon, tileClassName: tile.className, title: definition.title };
    const hasRows = !!rows && rows.rows.length > 0;

    let actions: ReactNode = null;
    if (isTable && rows && hasRows) {
        const downloadMenu = (
            <FigureDownload columns={rows.columns.map(columnLabel)} generating={generating} onDownload={download} />
        );
        actions = (
            <>
                {downloadMenu}
                <FigureDialog header={{ ...header, meta }} actions={downloadMenu}>
                    <FigureTable rows={rows} copyCsv={copyCsv} allRecords />
                </FigureDialog>
            </>
        );
    }

    let body: ReactNode = <FigureStatus definition={definition} empty={!!rows} query={query} />;
    if (rows && hasRows) {
        body = isTable ? (
            <FigureTable rows={rows} limit={TABLE_ROWS} copyCsv={copyCsv} />
        ) : (
            <FigureList definition={definition} rows={rows} />
        );
    }

    const fileCard =
        fileFormat && (rows || !query.isError) ? (
            <TableFileCard
                format={fileFormat}
                fileName={fileName(fileFormat)}
                rows={rows ? rows.count : null}
                generating={generating}
                onDownload={() => void download(fileFormat)}
            />
        ) : null;

    return (
        <div className="my-3 flex flex-col gap-3">
            {fileCard && fileCardSlot ? createPortal(fileCard, fileCardSlot) : fileCard}
            <FigureFrame {...header} meta={hasRows ? meta : null} busy={!rows && !query.isError} actions={actions}>
                {/* Keyed by the error, so a retry shows the bars again. */}
                <FigureReveal key={query.isError ? 'error' : 'rows'} loading={!rows && !query.isError}>
                    {body}
                </FigureReveal>
            </FigureFrame>
        </div>
    );
}
